import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DrawerPanel } from '../ui/DrawerPanel';

interface RemoteLockDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  seniorId: string;
  seniorName: string;
  isLocked: boolean;
  onLock: () => Promise<void>;
  onUnlock: () => Promise<void>;
}

/**
 * Remote Lock / Unlock panel.
 *
 * ⚠️ NOT RENDERED TODAY. Gated off by FEATURES.REMOTE_LOCK_ENABLED in
 * src/pages/Dashboard/index.tsx. Kept intact for the Wave 1.5 rebuild.
 * Its i18n strings were pulled from locales/{en,fr}.json so they stop
 * shipping in the bundle — they live in src/i18n/parked/remoteLock.wave1-5.json
 * and MUST be merged back before the flag is flipped, or every label here
 * renders as a raw key.
 *
 * Spec: AidFone_Johnley_Briefing_March2026.docx §3
 * - Warning text
 * - Type "LOCK" to enable the red lock button (confirmation gate)
 * - When phone is locked, panel shows unlock button instead
 *
 * Backend wiring (onLock / onUnlock callbacks) is stubbed at the dashboard level
 * until the FCM edge function is deployed (Phase 7, deferred).
 */
export const RemoteLockDrawer: FC<RemoteLockDrawerProps> = ({
  isOpen,
  onClose,
  seniorName,
  isLocked,
  onLock,
  onUnlock,
}) => {
  const { t } = useTranslation();
  const [confirmInput, setConfirmInput] = useState('');
  const [working, setWorking] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canLock = confirmInput === 'LOCK';

  const handleLock = async () => {
    setWorking(true);
    setError(null);
    try {
      await onLock();
      setConfirmInput('');
      onClose();
    } catch (err) {
      console.error('[RemoteLock] Failed:', err);
      setError(err instanceof Error ? err.message : t('drawers.remoteLock.lockFailed'));
    } finally {
      setWorking(false);
    }
  };

  const handleUnlock = async () => {
    setWorking(true);
    setError(null);
    try {
      await onUnlock();
      onClose();
    } catch (err) {
      console.error('[RemoteUnlock] Failed:', err);
      setError(err instanceof Error ? err.message : t('drawers.remoteLock.unlockFailed'));
    } finally {
      setWorking(false);
    }
  };

  if (isLocked) {
    return (
      <DrawerPanel
        isOpen={isOpen}
        onClose={onClose}
        title={t('drawers.remoteLock.lockedTitle')}
      >
        <div className="flex flex-col items-center text-center py-6">
          <div className="w-20 h-20 rounded-full bg-red-100 flex items-center justify-center mb-4">
            <span className="material-symbols-outlined text-red-600" style={{ fontSize: '40px' }}>
              lock
            </span>
          </div>
          <p className="text-sm text-brand-text-muted mb-6">
            {t('drawers.remoteLock.lockedDescription', { name: seniorName })}
          </p>

          {error && (
            <div className="w-full mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">
              {error}
            </div>
          )}

          <button
            onClick={handleUnlock}
            disabled={working}
            className="w-full py-3 bg-brand-primary text-white font-semibold rounded-xl disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {working
              ? t('drawers.remoteLock.unlocking')
              : t('drawers.remoteLock.unlockNow', { name: seniorName })}
          </button>
          <button
            onClick={onClose}
            className="mt-2 py-2 text-sm text-brand-text-muted hover:text-brand-text"
          >
            {t('drawers.remoteLock.cancel')}
          </button>
        </div>
      </DrawerPanel>
    );
  }

  return (
    <DrawerPanel isOpen={isOpen} onClose={onClose} title={t('drawers.remoteLock.title')}>
      <div className="flex flex-col gap-4">
        <div className="p-4 rounded-xl bg-red-50 border border-red-200">
          <p className="text-sm text-red-900 leading-relaxed">
            {t('drawers.remoteLock.warning', { name: seniorName })}
          </p>
        </div>

        <label className="flex flex-col gap-2">
          <span className="text-sm font-medium text-brand-text">
            {t('drawers.remoteLock.typeLockPrompt')}
          </span>
          <input
            type="text"
            value={confirmInput}
            onChange={(e) => setConfirmInput(e.target.value)}
            placeholder={t('drawers.remoteLock.typeLockPlaceholder')}
            className="px-4 py-3 rounded-xl border border-brand-border bg-white text-brand-text focus:border-red-500 focus:ring-2 focus:ring-red-500/20 outline-none"
            autoFocus
          />
        </label>

        {error && (
          <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">
            {error}
          </div>
        )}

        <button
          onClick={handleLock}
          disabled={!canLock || working}
          className="py-3 bg-red-600 text-white font-bold rounded-xl transition-opacity disabled:cursor-not-allowed"
          style={{ opacity: canLock && !working ? 1 : 0.4 }}
        >
          {working
            ? t('drawers.remoteLock.locking')
            : t('drawers.remoteLock.lockNow', { name: seniorName })}
        </button>
        <button
          onClick={onClose}
          className="py-2 text-sm text-brand-text-muted hover:text-brand-text"
        >
          {t('drawers.remoteLock.cancel')}
        </button>
      </div>
    </DrawerPanel>
  );
};

export default RemoteLockDrawer;
